import React, { useState } from 'react';

/**
 * Displays Visual Circling calculation results returned by
 * POST /api/circling-calculation.
 *
 * Shows a per-category obstacle table with OCA, clearance, critical flag and
 * the computed minimum circling altitude, analogous to TaaResultsPanel.
 *
 * Props:
 *   circlingAnalysis      – response from POST /api/circling-calculation
 *                           { summary, results: [...] }
 *   circlingError         – optional error string
 *   showCirclingAreas     – boolean controlling map overlay visibility
 *   setShowCirclingAreas  – setter for the above
 */
export default function CirclingResultsPanel({
  circlingAnalysis,
  circlingError,
  showCirclingAreas,
  setShowCirclingAreas,
}) {
  const getCategoryLabel = (category) =>
    category != null
      ? `Cat ${category === 'custom' ? 'Custom' : category.toUpperCase()}`
      : '';
  const [sortConfig, setSortConfig] = useState({ key: 'ocaM', direction: 'desc' });

  if (!circlingAnalysis && !circlingError) return null;

  const tdStyle = { padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' };

  const handleSort = (key) =>
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  const renderSortIndicator = (key) =>
    sortConfig.key === key
      ? <span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
      : <span style={{ opacity: 0.3, marginLeft: 4 }}>▼</span>;

  const thStyle = (key) => ({
    padding: 6,
    border: '1px solid #80b3c4',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    background: sortConfig.key === key ? '#b3d4c8' : '#d6f0e8',
    cursor: 'pointer',
    userSelect: 'none',
  });

  const sortRows = (rows) => {
    const key = sortConfig.key;
    const dir = sortConfig.direction;
    return [...rows].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return dir === 'asc' ? 1 : -1;
      if (bv == null) return dir === 'asc' ? -1 : 1;
      if (typeof av === 'number' && typeof bv === 'number')
        return dir === 'asc' ? av - bv : bv - av;
      av = String(av); bv = String(bv);
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  };

  const renderObstacleTable = (rows, idx) => {
    if (!rows || rows.length === 0) return null;
    const sorted = sortRows(rows.map((r, i) => ({ ...r, _id: `circ-${idx}-${i}` })));
    return (
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '860px', width: '100%' }}>
          <thead>
            <tr style={{ background: '#d6f0e8' }}>
              <th style={thStyle('obstacleName')}   onClick={() => handleSort('obstacleName')}>Obstacle Name{renderSortIndicator('obstacleName')}</th>
              <th style={thStyle('obstacleAltM')}   onClick={() => handleSort('obstacleAltM')}>Alt (m){renderSortIndicator('obstacleAltM')}</th>
              <th style={thStyle('obstacleAltFt')}  onClick={() => handleSort('obstacleAltFt')}>Alt (ft){renderSortIndicator('obstacleAltFt')}</th>
              <th style={thStyle('mocM')}            onClick={() => handleSort('mocM')}>MOC (m){renderSortIndicator('mocM')}</th>
              <th style={thStyle('mocFt')}           onClick={() => handleSort('mocFt')}>MOC (ft){renderSortIndicator('mocFt')}</th>
              <th style={thStyle('ocaM')}            onClick={() => handleSort('ocaM')}>OCA (m){renderSortIndicator('ocaM')}</th>
              <th style={thStyle('ocaFt')}           onClick={() => handleSort('ocaFt')}>OCA (ft){renderSortIndicator('ocaFt')}</th>
              <th style={thStyle('roundedAltM')}     onClick={() => handleSort('roundedAltM')}>Min Alt (m){renderSortIndicator('roundedAltM')}</th>
              <th style={thStyle('roundedAltFt')}    onClick={() => handleSort('roundedAltFt')}>Min Alt (ft){renderSortIndicator('roundedAltFt')}</th>
              <th style={thStyle('distFromHullM')}   onClick={() => handleSort('distFromHullM')}>Dist from Area (m){renderSortIndicator('distFromHullM')}</th>
              <th style={thStyle('clearanceM')}      onClick={() => handleSort('clearanceM')}>Clearance (m){renderSortIndicator('clearanceM')}</th>
              <th style={thStyle('clearanceFt')}     onClick={() => handleSort('clearanceFt')}>Clearance (ft){renderSortIndicator('clearanceFt')}</th>
              <th style={thStyle('isCritical')}      onClick={() => handleSort('isCritical')}>Critical{renderSortIndicator('isCritical')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, rowIdx) => {
              const isControlling = rowIdx === 0;
              const baseBg = isControlling ? '#c8e8d8' : 'white';
              return (
                <tr
                  key={row._id}
                  style={{ background: baseBg }}
                  onMouseEnter={e => { if (!isControlling) e.currentTarget.style.background = '#eafaf3'; }}
                  onMouseLeave={e => { if (!isControlling) e.currentTarget.style.background = baseBg; }}
                >
                  <td style={tdStyle}>{row.obstacleName}</td>
                  <td style={tdStyle}>{row.obstacleAltM != null ? row.obstacleAltM : '—'}</td>
                  <td style={tdStyle}>{row.obstacleAltFt != null ? row.obstacleAltFt : '—'}</td>
                  <td style={{ ...tdStyle, color: '#555' }}>{row.mocM != null ? `${row.mocM} m` : '—'}</td>
                  <td style={{ ...tdStyle, color: '#555' }}>{row.mocFt != null ? `${row.mocFt} ft` : '—'}</td>
                  <td style={tdStyle}>{row.ocaM != null ? row.ocaM : '—'}</td>
                  <td style={tdStyle}>{row.ocaFt != null ? row.ocaFt : '—'}</td>
                  <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#0d4f3c' }}>
                    {row.roundedAltM != null ? row.roundedAltM : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: isControlling ? 'bold' : 'normal', color: '#0d4f3c' }}>
                    {row.roundedAltFt != null ? row.roundedAltFt : '—'}
                  </td>
                  <td style={tdStyle}>{row.distFromHullM != null ? row.distFromHullM : '—'}</td>
                  <td style={{ ...tdStyle, color: row.clearanceM != null && row.clearanceM < 0 ? '#d32f2f' : '#2e7d32' }}>
                    {row.clearanceM != null ? row.clearanceM : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: row.clearanceFt != null && row.clearanceFt < 0 ? '#d32f2f' : '#2e7d32' }}>
                    {row.clearanceFt != null ? row.clearanceFt : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: row.isCritical ? '#d32f2f' : 'inherit', fontWeight: row.isCritical ? 'bold' : 'normal' }}>
                    {row.isCritical ? 'Yes' : 'No'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const results = (circlingAnalysis?.results || []).filter(r => r.obsRows?.length > 0);
  if (!results.length && !circlingError) return null;

  return (
    <div style={{ marginTop: 10, background: '#d6f0e8', padding: 16, borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0, color: '#0d4f3c' }}>
          Circling Calculation Results
        </h3>
        {setShowCirclingAreas && (
          <button
            onClick={() => setShowCirclingAreas(v => !v)}
            title="Toggle circling protected areas on the map"
            style={{
              padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              background: showCirclingAreas ? '#0d4f3c' : '#d6f0e8',
              color: showCirclingAreas ? '#fff' : '#0d4f3c',
              border: '1px solid #0d4f3c', whiteSpace: 'nowrap',
            }}
          >
            🗺 {showCirclingAreas ? 'Hide circling areas' : 'Show circling areas'}
          </button>
        )}
        {circlingAnalysis?.summary && (
          <span style={{ fontSize: 12, color: '#555' }}>
            {circlingAnalysis.summary.entriesEvaluated} categor{circlingAnalysis.summary.entriesEvaluated !== 1 ? 'ies' : 'y'} evaluated,&nbsp;
            {circlingAnalysis.summary.totalObstacles} obstacle{circlingAnalysis.summary.totalObstacles !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {circlingError && (
        <div style={{ color: '#b30000', background: '#ffebee', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <b>Circling Error:</b> {circlingError}
        </div>
      )}

      {results.length === 0 ? (
        <p style={{ color: '#555' }}>
          {circlingAnalysis?.message || 'No circling configurations found.'}
        </p>
      ) : (
        results.map((result) => {
          const idx = circlingAnalysis.results.indexOf(result);
          const inp = result.input || {};
          const hasCritical = result.criticalCount > 0;
          const catLabel = getCategoryLabel(result.category);
          return (
            <details key={idx} style={{ marginBottom: 12, background: 'white', padding: 12, borderRadius: 4 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#0d4f3c' }}>
                {[inp.procedure_id, catLabel].filter(Boolean).join(' / ')}
                {[inp.procedure_id, catLabel].some(Boolean) ? ' – ' : ''}
                {inp.airport_code || '—'}
                {(() => {
                  const rwys = inp.runway_designations?.length > 0
                    ? inp.runway_designations.join(', ')
                    : inp.runway_designation;
                  return rwys ? ` / RWY ${rwys}` : '';
                })()}
                {(inp.circling_restriction === 'L' || inp.circling_restriction === 'R') ? ` (${inp.circling_restriction} restriction)` : ''}
                {inp.radius_nm != null ? ` / ${inp.radius_nm} NM` : ''}
                {result.minimum?.formatted ? (
                  <span style={{ marginLeft: 12, color: '#0d4f3c', fontWeight: 'bold' }}>
                    → {result.minimum.formatted}
                  </span>
                ) : null}
                {hasCritical
                  ? <span style={{ marginLeft: 10, color: '#d32f2f', fontWeight: 'bold' }}>⚠ Impact ({result.criticalCount} critical)</span>
                  : (result.obsRows?.length > 0
                      ? <span style={{ marginLeft: 10, color: '#2e7d32' }}>✓ No impact</span>
                      : null)
                }
                {inp.error && (
                  <span style={{ color: '#d32f2f', marginLeft: 8 }}>⚠ {inp.error}</span>
                )}
              </summary>

              {/* Input parameters */}
              <div style={{ marginTop: 8, marginBottom: 8, fontSize: '12px', background: '#eafaf3', padding: 8, borderRadius: 4 }}>
                <b>Input Parameters</b>
                <table style={{ borderCollapse: 'collapse', marginTop: 4, width: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={tdStyle}>Airport</td>
                      <td style={tdStyle}><b>{inp.airport_code || '—'}</b></td>
                      <td style={tdStyle}>Procedure</td>
                      <td style={tdStyle}><b>{inp.procedure_id || '—'}</b></td>
                      <td style={tdStyle}>Runway(s)</td>
                      <td style={tdStyle}><b>{inp.runway_designations?.length > 0 ? inp.runway_designations.join(', ') : (inp.runway_designation || '—')}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>Category</td>
                      <td style={tdStyle}><b>{catLabel}</b></td>
                      <td style={tdStyle}>IAS</td>
                      <td style={tdStyle}><b>{inp.ias_kts != null ? `${inp.ias_kts} kts` : '—'}</b></td>
                      <td style={tdStyle}>TAS (+25)</td>
                      <td style={tdStyle}><b>{inp.tas_kts != null ? `${inp.tas_kts} kts` : '—'}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>Radius</td>
                      <td style={tdStyle}><b>{inp.radius_nm != null ? `${inp.radius_nm} NM (${inp.radius_m} m)` : '—'}</b></td>
                      <td style={tdStyle}>MOC</td>
                      <td style={tdStyle}><b>{inp.moc_m != null ? `${inp.moc_m} m / ${inp.moc_ft} ft` : '—'}</b></td>
                      <td style={tdStyle}>Restriction</td>
                      <td style={tdStyle}><b>{inp.circling_restriction || 'None'}</b></td>
                    </tr>
                    <tr>
                      <td style={tdStyle}>AD Elevation</td>
                      <td style={tdStyle}><b>{inp.ad_elev_ft != null ? `${inp.ad_elev_ft} ft` : '—'}</b></td>
                      <td style={tdStyle}>Published Min</td>
                      <td style={{ ...tdStyle, color: '#0d4f3c', fontWeight: 'bold' }}>
                        {inp.stored_alt_ft != null ? `${inp.stored_alt_ft} ft` : '—'}
                      </td>
                      <td style={tdStyle}>Computed Min</td>
                      <td style={{ ...tdStyle, color: '#0d4f3c', fontWeight: 'bold' }}>
                        {result.minimum?.formatted || '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Obstacle table */}
              <div style={{ marginTop: 8, border: '1px solid #80b3c4', borderRadius: 4, padding: 8 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                  Obstacles inside protected area
                  <span style={{ marginLeft: 8, fontWeight: 'normal', color: hasCritical ? '#d32f2f' : '#555' }}>
                    ({result.criticalCount || 0} critical)
                  </span>
                </div>
                {result.obsRows && result.obsRows.length > 0
                  ? renderObstacleTable(result.obsRows, idx)
                  : <p style={{ color: '#666', fontStyle: 'italic', margin: 4 }}>No obstacles within protected area.</p>
                }
              </div>
            </details>
          );
        })
      )}
    </div>
  );
}
